import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { isEmpty } from 'lodash'
import { IconKey, IconLock, IconTrash, IconPencil, IconCopy, IconX } from '@tabler/icons-react'
import { TextInput } from '@/components/forms/TextInput'

import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/toasts/toast.helpers'
import {
  fetchConnectorCredentials,
  fetchConnectorRegistry,
  createConnectorCredential,
  updateConnectorCredential,
  deleteConnectorCredential,
  revealConnectorCredential,
} from '@/reducers/voiceAgents/agentConnectorCredentials.actions'
import { connectorNames, indexRegistry } from '@/reducers/voiceAgents/connectorRegistry'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import EmptyState from './EmptyState'

const FIELD_PREFIX = 'f_'
// Sentinel shown for existing secrets — the real value never leaves the backend.
// A field still equal to this on save is treated as unchanged (kept).
const MASK = '**********'

const prettyLabel = (name) =>
  name
    .split('_')
    .map((w) =>
      w === 'id' || w === 'api' || w === 'url'
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ')

// `maskDefaults` prefills each field with the MASK sentinel (edit view: an
// existing secret shows as ********, mandatory + red star, real value never sent
// to the FE). Create view leaves them empty with a placeholder.
const secretFieldSchema = (spec, { maskDefaults } = {}) => {
  if (isEmpty(spec.fields)) {
    return [
      {
        label: 'Fields (JSON)',
        name: 'fields_json',
        type: 'textArea',
        required: true,
        ...(maskDefaults ? { defaultValue: MASK } : { placeholder: '{ "api_key": "..." }' }),
      },
    ]
  }
  return spec.fields.map((f) => ({
    label: prettyLabel(f.name),
    name: `${FIELD_PREFIX}${f.name}`,
    type: f.secret ? 'password' : 'text',
    required: f.required,
    ...(maskDefaults ? { defaultValue: MASK } : { placeholder: 'secret value' }),
  }))
}

// Collect the fields the user actually changed. A value still equal to MASK
// (untouched existing secret) is skipped so the backend keeps it.
const collectFields = (spec, values) => {
  if (isEmpty(spec.fields)) {
    const raw = (values.fields_json || '').trim()
    return raw && raw !== MASK ? JSON.parse(raw) : {}
  }
  return spec.fields.reduce((acc, f) => {
    const v = String(values[`${FIELD_PREFIX}${f.name}`] || '').trim()
    if (v && v !== MASK) acc[f.name] = v
    return acc
  }, {})
}

// Breadcrumb header used by the inline create/edit views — mirrors the
// Integrations editor ("Integrations / <name>" as a link + optional actions).
const EditorHeader = ({ name, onBack, actions }) => (
  <div className="page-header">
    <div className="voice-agents__cc-crumbs">
      <button type="button" className="voice-agents__cc-crumb-link" onClick={onBack}>
        Connector credentials
      </button>
      <span className="voice-agents__cc-crumb-sep">/</span>
      <span className="voice-agents__cc-crumb-current">{name}</span>
    </div>
    {actions ? <div className="flex-align-center gap">{actions}</div> : null}
  </div>
)

// Inline create view (a full page within the tab, not a modal). The credential
// is scoped to the agent whose page we're on — `voiceAgentId` comes from the
// navigation context, so it's never a form input.
const CreateConnectorCredentialView = ({ orgId, voiceAgentId, registry, onBack, onSaved }) => {
  const dispatch = useDispatch()
  const [connector, setConnector] = useState('')
  const connectorOptions = useMemo(
    () => connectorNames(registry).map((connector) => ({ value: connector, label: connector })),
    [registry]
  )

  const schema = useMemo(() => {
    const connectorField = {
      label: 'Connector',
      name: 'connector',
      type: 'select',
      options: connectorOptions,
      required: true,
      defaultValue: connector,
      placeholder: 'Select a connector...',
      // Portal the dropdown to <body> so it isn't clipped by the page's
      // overflow scroll container — it floats above with a clear shadow.
      menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
      menuPosition: 'fixed',
      onChange: (val) => setConnector(val && val.value ? val.value : val || ''),
    }
    const spec = registry[connector]
    if (!spec) {
      return [connectorField]
    }
    return [
      connectorField,
      {
        label: 'Auth Type',
        name: 'auth_type',
        type: 'text',
        defaultValue: spec.auth,
        disabled: true,
      },
      ...secretFieldSchema(spec),
    ]
  }, [connector, connectorOptions, registry])

  const handleSubmit = async (values) => {
    if (!voiceAgentId) {
      toast.error('Cannot resolve this agent — open the agent page before adding credentials')
      return
    }
    // Submitting with no connector picked (or one the registry no longer serves)
    // would read `.auth` off undefined and take down the page.
    const spec = registry[connector]
    if (!spec) {
      toast.error('Select a connector first')
      return
    }
    let fields
    try {
      fields = collectFields(spec, values)
    } catch (e) {
      toast.error('Fields must be valid JSON', { cause: e })
      return
    }
    try {
      await dispatch(
        createConnectorCredential(
          {
            connector,
            // Always scoped to the agent currently open (guarded above).
            voice_agent_id: voiceAgentId,
            auth_type: spec.auth,
            fields,
          },
          orgId
        )
      )
      onSaved()
    } catch (error) {
      toast.error(error.message || 'Failed to create credential')
    }
  }

  return (
    <div className="voice-agents__cc-page" data-testid="connector-credentials-create">
      <EditorHeader name="New credential" onBack={onBack} />
      <Card>
        <AbstractForm
          key={connector || 'none'}
          schema={schema}
          existingValues={{ connector }}
          buttonLabel="Create credential"
          closeButtonLabel="Cancel"
          onClose={onBack}
          onSubmit={handleSubmit}
        />
      </Card>
    </div>
  )
}

// A locked read-only field (greyed). Rendered in the same secret-row layout as
// the editable fields — with a spacer standing in for the copy/edit icons — so
// every input on the page lines up to the exact same width.
const LockedField = ({ label, value }) => (
  <FormField className="voice-agents__cc-field">
    <label>{label}</label>
    <div className="voice-agents__cc-secret-row">
      <TextInput className="voice-agents__cc-input" value={value} disabled readOnly />
      <span className="voice-agents__cc-icon-spacer" aria-hidden="true" />
    </div>
  </FormField>
)

// Inline edit view — every field is greyed/locked by default with copy + edit
// (pencil) icons. Copy fetches the value over TLS (audited) straight to the
// clipboard. Edit clears the field to a blank white input for a brand-new value
// (we never fetch the real key back into the form). Only edited fields are sent.
const EditConnectorCredentialView = ({ row, orgId, registry, onBack, onSaved }) => {
  const dispatch = useDispatch()
  const spec = registry[row.connector] || { fields: [] }
  const [active, setActive] = useState(row.active)
  const [toggling, setToggling] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState({}) // fieldName -> new value (presence = edit mode)

  const startEdit = (name) => setEditing((p) => ({ ...p, [name]: '' }))
  const cancelEdit = (name) =>
    setEditing((p) => {
      const next = { ...p }
      delete next[name]
      return next
    })
  const setVal = (name, v) => setEditing((p) => ({ ...p, [name]: v }))

  // Copy one field's value straight to the clipboard. Fetched over TLS
  // (server-side decrypt, audited), written to the clipboard, and never rendered
  // or stored in state — it only lives in this local const during the call.
  const copyField = async (name) => {
    try {
      // Fetch ONLY the field we're copying — never the whole secret set.
      const data = await dispatch(revealConnectorCredential(row.id, orgId, name))
      const value = data?.value
      if (!value) {
        toast.error('No value to copy')
        return
      }
      await navigator.clipboard.writeText(value)
      toast.success(`${prettyLabel(name)} copied to clipboard`)
    } catch (error) {
      toast.error(error.message || 'Failed to copy')
    }
  }

  const handleToggleActive = async () => {
    setToggling(true)
    try {
      await dispatch(updateConnectorCredential(row.id, { active: !active }, orgId))
      setActive(!active)
    } catch (error) {
      toast.error(error.message || 'Failed to update credential')
    } finally {
      setToggling(false)
    }
  }

  const changed = Object.entries(editing).reduce((acc, [k, v]) => {
    const t = String(v).trim()
    if (t) acc[k] = t
    return acc
  }, {})

  const handleSave = async () => {
    if (isEmpty(changed)) {
      onBack()
      return
    }
    setSaving(true)
    try {
      await dispatch(updateConnectorCredential(row.id, { fields: changed }, orgId))
      onSaved()
    } catch (error) {
      toast.error(error.message || 'Failed to update credential')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="voice-agents__cc-page" data-testid="connector-credentials-edit">
      <EditorHeader
        name={row.connector}
        onBack={onBack}
        actions={
          <Button critical={active} onClick={handleToggleActive} loading={toggling}>
            {active ? 'Deactivate' : 'Activate'}
          </Button>
        }
      />
      <Card>
        <LockedField label="Connector" value={row.connector} />
        <LockedField label="Auth Type" value={row.auth} />
        <LockedField label="Scope" value={row.scope} />

        {spec.fields.map((f) => {
          const { name, required } = f
          const isEditing = editing[name] !== undefined
          return (
            <FormField key={name} className="voice-agents__cc-field">
              <label>
                {prettyLabel(name)}
                {required ? <span className="voice-agents__cc-req"> *</span> : null}
              </label>
              <div className="voice-agents__cc-secret-row">
                {isEditing ? (
                  <TextInput
                    key={`${name}-edit`}
                    type="password"
                    autoComplete="new-password"
                    className="voice-agents__cc-input"
                    value={editing[name]}
                    placeholder="Enter a new value"
                    onChange={(e) => setVal(name, e.target.value)}
                    autoFocus
                  />
                ) : (
                  <TextInput
                    key={`${name}-lock`}
                    type="password"
                    autoComplete="new-password"
                    className="voice-agents__cc-input"
                    value={MASK}
                    disabled
                    readOnly
                  />
                )}
                <Button
                  basic
                  small
                  square
                  tooltip="Copy value"
                  aria-label={`Copy ${prettyLabel(name)}`}
                  onClick={() => copyField(name)}
                  icon={<IconCopy />}
                />
                {isEditing ? (
                  <Button
                    basic
                    small
                    square
                    tooltip="Cancel edit"
                    aria-label={`Cancel editing ${prettyLabel(name)}`}
                    onClick={() => cancelEdit(name)}
                    icon={<IconX />}
                  />
                ) : (
                  <Button
                    basic
                    small
                    square
                    tooltip="Edit value"
                    aria-label={`Edit ${prettyLabel(name)}`}
                    onClick={() => startEdit(name)}
                    icon={<IconPencil />}
                  />
                )}
              </div>
            </FormField>
          )
        })}

        <div className="voice-agents__cc-form-buttons">
          <Button onClick={onBack}>Cancel</Button>
          <Button primary onClick={handleSave} loading={saving} disabled={isEmpty(changed)}>
            Save
          </Button>
        </div>
      </Card>
    </div>
  )
}

// Top-level Credentials tab (?tab=credentials), admin org (org 1) only. Mirrors
// the Integrations flow: a list page, and clicking a row opens a full-page
// inline editor (no modal). Delete is a trash action with a small confirm.
const ConnectorCredentialsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const isOrg1 = useSelector((state) => state.currentUser.own_organization_id) === 1
  const selectedAgent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  // Credentials live under the org that OWNS this agent (runtime resolution
  // keys on it), so both come from the agent's config response — never from
  // the caller's org. Integer PK scopes new credentials to this agent
  // automatically (no manual "Voice Agent ID" input).
  const orgId = selectedAgent?.organizationId || undefined
  const voiceAgentId = selectedAgent?.voiceAgentId || null
  const agentLoading = useSelector((state) => Boolean(state.voiceAgents.loading?.agentDetail))

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  // Connector field shapes come from voice-agents (AVR-620) — the form can't be
  // rendered without them, so the editors stay gated until this lands.
  // Three-state, not `isEmpty(registry)`: a response that succeeds with no
  // connectors is "ready and empty", not "still loading", and the memoised
  // promise means it would never be retried out of a loading state.
  const [registry, setRegistry] = useState({})
  const [registryState, setRegistryState] = useState('loading') // loading | ready | failed
  // True once the first list fetch has landed — the cc-restore effect must not
  // judge a deep-linked credential id against the not-yet-loaded (empty) list.
  const [loaded, setLoaded] = useState(false)
  const [view, setView] = useState({ mode: 'list' }) // list | create | edit

  // Mirror the editor state into a `cc` URL param so the agent page can hide its
  // header/tabs while a connector is open (a focused, full-page editor).
  const setCc = (val) => {
    const q = queryString.parse(location.search)
    if (val) {
      q.cc = val
    } else {
      delete q.cc
    }
    history.replace({ pathname: location.pathname, search: queryString.stringify(q) })
  }
  const openCreate = () => {
    setView({ mode: 'create' })
    setCc('new')
  }
  const openEdit = (row) => {
    setView({ mode: 'edit', row })
    setCc(String(row.id))
  }

  const load = async () => {
    setLoading(true)
    try {
      // Scope to the current agent — the tab only shows this agent's credentials.
      const data = await dispatch(fetchConnectorCredentials({ orgId, voiceAgentId }))
      setItems(Array.isArray(data?.items) ? data.items : [])
      setLoaded(true)
    } catch (error) {
      toast.error(error.message || 'Failed to load credentials')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // No agent PK yet (detail still loading, or an agent whose config carries no
    // voiceAgentId) → do NOT fetch. An unscoped list returns every credential in
    // the org, so the tab would show, and let you delete, another agent's rows.
    if (isOrg1 && voiceAgentId) {
      load()
    }
    // eslint-disable-next-line
  }, [orgId, isOrg1, voiceAgentId])

  // Registry is static per deploy and agent-independent — fetch it once (the
  // action memoises the promise across mounts).
  useEffect(() => {
    if (!isOrg1) {
      return
    }
    dispatch(fetchConnectorRegistry())
      .then((payload) => {
        setRegistry(indexRegistry(payload))
        setRegistryState('ready')
      })
      .catch(() => setRegistryState('failed'))
    // eslint-disable-next-line
  }, [isOrg1])

  const remove = async (row) => {
    try {
      await dispatch(deleteConnectorCredential(row.id, orgId))
      await load()
    } catch (error) {
      toast.error(error.message || 'Failed to delete credential')
    }
  }

  const toRow = (item) => ({
    id: item.crm_credential_id,
    connector: item.connector,
    auth: item.auth_type,
    active: item.active,
    scope: item.voice_agent_id ? `agent ${item.voice_agent_id}` : 'org-wide',
  })

  // Restore the editor from a deep link / refresh — AT MOST ONCE. After the
  // initial `cc` is resolved (or there was none), user actions own the
  // view/param sync; re-running on later renders races backToList's param
  // clearing (view flips to list while `cc` is still in the URL for a tick)
  // and bounces the user straight back into the editor they just saved from
  // (AVR-514). `new` reopens the create view immediately; a credential id
  // reopens that credential once the list has loaded, and a stale id clears
  // the param so the agent page chrome (header/tabs) comes back.
  const ccRestoreDone = useRef(false)
  // Agent switches don't remount this component (load() re-keys on
  // voiceAgentId at the fetch effect above) — re-arm the restore so a fresh
  // deep link for the newly selected agent still resolves.
  useEffect(() => {
    ccRestoreDone.current = false
  }, [voiceAgentId])
  useEffect(() => {
    if (ccRestoreDone.current || view.mode !== 'list') {
      return
    }
    const { cc } = queryString.parse(location.search)
    if (!cc) {
      ccRestoreDone.current = true
      return
    }
    if (cc === 'new') {
      ccRestoreDone.current = true
      setView({ mode: 'create' })
      return
    }
    if (!loaded) {
      // Keep the restore pending until the first list fetch lands.
      return
    }
    ccRestoreDone.current = true
    const item = items.find((i) => String(i.crm_credential_id) === String(cc))
    if (item) {
      setView({ mode: 'edit', row: toRow(item) })
    } else {
      setCc(null)
    }
    // eslint-disable-next-line
  }, [view.mode, items, loaded, location.search])

  if (!isOrg1) {
    return (
      <div className="voice-agents__cc-page" data-testid="connector-credentials-page">
        <EmptyState
          icon={IconLock}
          title="Restricted"
          hint="Connector credentials are available to the Balto admin org only."
          dataTestId="connector-credentials-restricted"
        />
      </div>
    )
  }

  // Every credential belongs to exactly one agent, so without this agent's PK
  // there is nothing this tab may show — and nothing it may safely delete. A
  // healthy agent passes through here for as long as its detail is in flight,
  // so that window reads as loading rather than as a failure.
  if (!voiceAgentId) {
    return (
      <div className="voice-agents__cc-page" data-testid="connector-credentials-page">
        <EmptyState
          icon={IconKey}
          title={agentLoading ? 'Loading agent...' : 'Agent not resolved yet'}
          hint={
            agentLoading
              ? ''
              : "This agent has no AI agent record loaded, so its credentials can't be listed. Reopen the agent from the Studio list; if it persists, the agent isn't provisioned."
          }
          dataTestId="connector-credentials-no-agent"
        />
      </div>
    )
  }

  const backToList = () => {
    setView({ mode: 'list' })
    setCc(null)
  }
  const savedThenList = () => {
    backToList()
    load()
  }

  // Both editors render their fields off the registry — without it there is
  // nothing to draw, so say so instead of showing an empty form. "Ready but
  // empty" is a served spec with no connectors in it, not a pending fetch.
  if (view.mode !== 'list' && (registryState !== 'ready' || isEmpty(registry))) {
    const stillLoading = registryState === 'loading'
    return (
      <div className="voice-agents__cc-page">
        <EditorHeader name="Credential" onBack={backToList} />
        <EmptyState
          icon={IconKey}
          title={stillLoading ? 'Loading connectors...' : 'Connector registry unavailable'}
          hint={
            stillLoading
              ? ''
              : 'The voice-agents service served no connector field spec. Credentials cannot be edited without it — retry in a moment, and check that the service is up to date.'
          }
          dataTestId="connector-registry-unavailable"
        />
      </div>
    )
  }

  if (view.mode === 'create') {
    return (
      <CreateConnectorCredentialView
        orgId={orgId}
        voiceAgentId={voiceAgentId}
        registry={registry}
        onBack={backToList}
        onSaved={savedThenList}
      />
    )
  }
  if (view.mode === 'edit') {
    return (
      <EditConnectorCredentialView
        row={view.row}
        orgId={orgId}
        registry={registry}
        onBack={backToList}
        onSaved={savedThenList}
      />
    )
  }

  const columns = [
    { accessor: 'connector', label: 'Connector', isSearchable: true },
    { accessor: 'scope', label: 'Scope' },
    { accessor: 'auth', label: 'Auth' },
    { accessor: 'fields', label: 'Fields' },
    { accessor: 'status', label: 'Status' },
  ]

  const rows = items.map((item) => {
    const r = toRow(item)
    return {
      ...r,
      connector: {
        value: item.connector,
        as: (
          <button type="button" className="voice-agents__cc-link" onClick={() => openEdit(r)}>
            {item.connector}
          </button>
        ),
      },
      fields: { value: '', as: <code className="voice-agents__cc-value">••••••••</code> },
      status: {
        value: item.active ? 'active' : 'inactive',
        as: (
          <Pill small success={item.active} outlined={!item.active}>
            {item.active ? 'Active' : 'Inactive'}
          </Pill>
        ),
      },
    }
  })

  const actions = [
    {
      label: 'Delete',
      trigger: (row) => (
        <BasicModal
          trigger={(handleOpen) => (
            <Button small critical basic square onClick={handleOpen} tooltip="Delete credential">
              <IconTrash />
            </Button>
          )}
          title="Delete credential"
          size="tiny"
        >
          {(handleClose) => (
            <ModalActions onClose={handleClose} onSubmit={() => remove(row)} isDelete>
              <p>{`Delete the ${row.connector.value} credential (${row.scope})? This cannot be undone.`}</p>
            </ModalActions>
          )}
        </BasicModal>
      ),
    },
  ]

  return (
    <div className="voice-agents__cc-page" data-testid="connector-credentials-page">
      <header className="page-header">
        <h1>Connector credentials</h1>
        <Button primary onClick={openCreate} data-testid="cc-add">
          Add credential
        </Button>
      </header>

      {!loading && isEmpty(items) ? (
        <EmptyState
          icon={IconKey}
          title="No connector credentials yet"
          hint="Use “Add credential” to store a connector’s credentials. Values are encrypted at rest and resolved by scope."
          dataTestId="connector-credentials-empty"
        />
      ) : (
        <Card className="no-padding">
          <AdvancedTable
            defaultOrderBy="connector"
            loading={loading}
            columns={columns}
            rows={rows}
            actions={actions}
            stickyAction
            pagination
          />
        </Card>
      )}
    </div>
  )
}

export default ConnectorCredentialsPage
